import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import "./Login.css";

const API = "https://backend-9i6n.onrender.com";

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  // COMMON
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // REGISTER ONLY
  const [fullName, setFullName] = useState("");
  const [mobile, setMobile] = useState("");
  const [village, setVillage] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // VALIDATION ERRORS
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // UI STATE (from design)
  const [showPw, setShowPw] = useState(false);
  const [showCpw, setShowCpw] = useState(false);
  const [banner, setBanner] = useState(null); // { type: "success" | "error", text }

  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  // VALIDATION FUNCTIONS
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return "ई-मेल आवश्यक आहे";
    if (!emailRegex.test(email)) return "वैध ई-मेल प्रविष्ट करा";
    return "";
  };

  const validatePassword = (password) => {
    if (!password) return "पासवर्ड आवश्यक आहे";
    if (password.length < 6) return "पासवर्ड किमान ६ अक्षरांचा असावा";
    return "";
  };

  const validateFullName = (name) => {
    if (!name) return "पूर्ण नाव आवश्यक आहे";
    if (name.length < 3) return "नाव किमान ३ अक्षरांचे असावे";
    return "";
  };

  const validateMobile = (mobile) => {
    const mobileRegex = /^[6-9]\d{9}$/;
    if (!mobile) return "मोबाईल क्रमांक आवश्यक आहे";
    if (!mobileRegex.test(mobile)) return "वैध १० अंकी मोबाईल क्रमांक प्रविष्ट करा";
    return "";
  };

  const validateVillage = (village) => {
    if (!village) return "गावाचे नाव आवश्यक आहे";
    if (village.length < 2) return "गावाचे नाव किमान २ अक्षरांचे असावे";
    return "";
  };

  const validateConfirmPassword = (confirmPass, pass) => {
    if (!confirmPass) return "पासवर्डची पुष्टी आवश्यक आहे";
    if (confirmPass !== pass) return "पासवर्ड जुळत नाहीत";
    return "";
  };

  // HANDLE FIELD BLUR
  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
    validateField(field);
  };

  // VALIDATE INDIVIDUAL FIELD
  const validateField = (field) => {
    let error = "";

    switch (field) {
      case "email":
        error = validateEmail(email);
        break;
      case "password":
        error = validatePassword(password);
        break;
      case "fullName":
        error = validateFullName(fullName);
        break;
      case "mobile":
        error = validateMobile(mobile);
        break;
      case "village":
        error = validateVillage(village);
        break;
      case "confirmPassword":
        error = validateConfirmPassword(confirmPassword, password);
        break;
      default:
        break;
    }

    setErrors({ ...errors, [field]: error });
    return error === "";
  };

  // VALIDATE ALL FIELDS
  const validateAllFields = () => {
    const newErrors = {};

    newErrors.email = validateEmail(email);
    newErrors.password = validatePassword(password);

    if (!isLogin) {
      newErrors.fullName = validateFullName(fullName);
      newErrors.mobile = validateMobile(mobile);
      newErrors.village = validateVillage(village);
      newErrors.confirmPassword = validateConfirmPassword(confirmPassword, password);
    }

    setErrors(newErrors);

    // Mark all fields as touched
    const allTouched = {};
    Object.keys(newErrors).forEach((key) => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    // Check if any errors exist
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBanner(null);

    // Validate all fields
    if (!validateAllFields()) {
      setBanner({ type: "error", text: "कृपया खालील चुका दुरुस्त करा" });
      return;
    }

    setIsLoading(true);

    try {
      if (isLogin) {
        // 🔐 LOGIN
        const res = await axios.post(`${API}/api/auth/login`, {
          email,
          password,
        });

        login(res.data.token, res.data.user);

        if (res.data.user.role === "admin") {
          navigate("/admin/dashboard");
        } else {
          navigate("/profile");
        }
      } else {
        // 📝 REGISTER
        await axios.post(`${API}/api/auth/register`, {
          fullName,
          email,
          password,
          mobile,
          village,
        });

        resetForm();
        setIsLogin(true);
        setBanner({ type: "success", text: "नोंदणी यशस्वी झाली! कृपया लॉगिन करा." });
      }
    } catch (err) {
      setBanner({
        type: "error",
        text:
          err.response?.data?.message ||
          `${isLogin ? "लॉगिन" : "नोंदणी"} अयशस्वी झाली`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFullName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setMobile("");
    setVillage("");
    setErrors({});
    setTouched({});
    setShowPw(false);
    setShowCpw(false);
  };

  // SWITCH VIEW (segmented header)
  const switchTo = (loginView) => {
    if (loginView === isLogin) return;
    setIsLogin(loginView);
    resetForm();
    setBanner(null);
  };

  const inputClass = (field) =>
    `rk-input${touched[field] && errors[field] ? " input-error" : ""}`;

  return (
    <div className="rk-auth">
      {/* ---------- segmented switcher: लॉगिन / नोंदणी ---------- */}
      <div className="rk-switcher">
        <button
          type="button"
          className={`rk-tab${isLogin ? " active" : ""}`}
          onClick={() => switchTo(true)}
        >
          लॉगिन
        </button>
        <button
          type="button"
          className={`rk-tab${!isLogin ? " active" : ""}`}
          onClick={() => switchTo(false)}
        >
          नोंदणी
        </button>
      </div>

      <div className="rk-card-wrap">
        <div className="rk-card">
          {/* ---------- card head ---------- */}
          <div className="rk-card-head">
            <div className="rk-accent-bar"></div>
            <h1>{isLogin ? "पुन्हा स्वागत आहे" : "नवीन खाते तयार करा"}</h1>
            <p>
              {isLogin
                ? "कृपया पुढे जाण्यासाठी लॉगिन करा"
                : "सुरू करण्यासाठी नोंदणी करा"}
            </p>
          </div>

          <div className="rk-card-body">
            {/* ---------- submit banner ---------- */}
            {banner && (
              <div className={`rk-banner ${banner.type}`}>
                <span>{banner.type === "success" ? "✅" : "⚠️"}</span>
                <span>{banner.text}</span>
              </div>
            )}

            <form className="rk-form" onSubmit={handleSubmit} noValidate>
              {!isLogin && (
                <div className="rk-field">
                  <label>पूर्ण नाव</label>
                  <input
                    type="text"
                    className={inputClass("fullName")}
                    placeholder="उदा. राजेश कुमार शर्मा"
                    value={fullName}
                    onChange={(e) => {
                      setFullName(e.target.value);
                      if (touched.fullName) validateField("fullName");
                    }}
                    onBlur={() => handleBlur("fullName")}
                    disabled={isLoading}
                  />
                  {touched.fullName && errors.fullName && (
                    <div className="rk-err">
                      <span>⚠</span>
                      {errors.fullName}
                    </div>
                  )}
                </div>
              )}

              <div className="rk-field">
                <label>ई-मेल</label>
                <input
                  type="email"
                  className={inputClass("email")}
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (touched.email) validateField("email");
                  }}
                  onBlur={() => handleBlur("email")}
                  disabled={isLoading}
                />
                {touched.email && errors.email && (
                  <div className="rk-err">
                    <span>⚠</span>
                    {errors.email}
                  </div>
                )}
              </div>

              {!isLogin && (
                <div className="rk-row">
                  <div className="rk-field">
                    <label>मोबाईल क्रमांक</label>
                    <input
                      type="tel"
                      className={inputClass("mobile")}
                      placeholder="9876543210"
                      value={mobile}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                        setMobile(value);
                        if (touched.mobile) validateField("mobile");
                      }}
                      onBlur={() => handleBlur("mobile")}
                      disabled={isLoading}
                      maxLength="10"
                    />
                    {touched.mobile && errors.mobile && (
                      <div className="rk-err">
                        <span>⚠</span>
                        {errors.mobile}
                      </div>
                    )}
                  </div>

                  <div className="rk-field">
                    <label>गावाचे नाव</label>
                    <input
                      type="text"
                      className={inputClass("village")}
                      placeholder="उदा. रोषणखेडा"
                      value={village}
                      onChange={(e) => {
                        setVillage(e.target.value);
                        if (touched.village) validateField("village");
                      }}
                      onBlur={() => handleBlur("village")}
                      disabled={isLoading}
                    />
                    {touched.village && errors.village && (
                      <div className="rk-err">
                        <span>⚠</span>
                        {errors.village}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="rk-field">
                <label>पासवर्ड</label>
                <div className="rk-pw">
                  <input
                    type={showPw ? "text" : "password"}
                    className={inputClass("password")}
                    placeholder={isLogin ? "••••••••" : "किमान ६ अक्षरे"}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (touched.password) validateField("password");
                      if (touched.confirmPassword && !isLogin) validateField("confirmPassword");
                    }}
                    onBlur={() => handleBlur("password")}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="rk-eye"
                    onClick={() => setShowPw((s) => !s)}
                  >
                    {showPw ? "लपवा" : "दाखवा"}
                  </button>
                </div>
                {touched.password && errors.password && (
                  <div className="rk-err">
                    <span>⚠</span>
                    {errors.password}
                  </div>
                )}
              </div>

              {!isLogin && (
                <div className="rk-field">
                  <label>पासवर्डची पुष्टी करा</label>
                  <div className="rk-pw">
                    <input
                      type={showCpw ? "text" : "password"}
                      className={inputClass("confirmPassword")}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (touched.confirmPassword) validateField("confirmPassword");
                      }}
                      onBlur={() => handleBlur("confirmPassword")}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="rk-eye"
                      onClick={() => setShowCpw((s) => !s)}
                    >
                      {showCpw ? "लपवा" : "दाखवा"}
                    </button>
                  </div>
                  {touched.confirmPassword && errors.confirmPassword && (
                    <div className="rk-err">
                      <span>⚠</span>
                      {errors.confirmPassword}
                    </div>
                  )}
                </div>
              )}

              <button type="submit" className="rk-submit" disabled={isLoading}>
                {isLoading && <span className="rk-spinner"></span>}
                <span>
                  {isLoading
                    ? isLogin
                      ? "लॉगिन होत आहे..."
                      : "नोंदणी होत आहे..."
                    : isLogin
                    ? "लॉगिन करा"
                    : "नोंदणी करा"}
                </span>
              </button>

              <div className="rk-card-foot">
                {isLogin ? "खाते नाही आहे?" : "आधीच खाते आहे?"}
                <span
                  className="toggle-link"
                  onClick={() => switchTo(!isLogin)}
                >
                  {isLogin ? " नोंदणी करा" : " लॉगिन करा"}
                </span>
              </div>
            </form>
          </div>
        </div>

        <p className="rk-copyright">
          © ग्रामपंचायत रोषणखेडा · तालुका वरूड, जिल्हा अमरावती
        </p>
      </div>
    </div>
  );
};

export default Auth;
